(() => {
  'use strict';

  // This short-lived handoff only bridges a Theme Editor section replacement.
  // Visitor choices are never persisted to localStorage or sent off-device.
  const pendingSelections = new Map();

  function initializeMaterialLab(root) {
    if (root.dataset.amlReady === 'true') return;
    const data = root.querySelector('[data-aml-materials]');
    if (!data) return;
    let materials;
    try { materials = JSON.parse(data.textContent); } catch { return; }
    const uiData = root.querySelector('[data-aml-ui]');
    if (!uiData) return;
    let ui;
    try { ui = JSON.parse(uiData.textContent); } catch { return; }
    const formatUi = (key, values) => ui[key].replace(/\[([a-z_]+)\]/g, (token, name) => String(values[name] ?? token));
    if (!Array.isArray(materials)) return;
    root.dataset.empty = String(materials.length === 0);
    if (!materials.length) return;

    const ids = [
      'swatch-stage', 'material-cards', 'material-tabs', 'deck-tilt',
      'selected-index', 'selected-name', 'selected-category', 'selected-headline',
      'material-description', 'material-properties', 'application-list', 'application-material',
      'sample-checks', 'material-note', 'focus-sheet', 'material-status',
      'previous-material', 'next-material', 'inspect-texture', 'image-reference-note',
      'texture-dialog', 'texture-image', 'texture-title', 'texture-scale',
      'texture-close', 'texture-error', 'copy-brief', 'brief-status',
      'motion-toggle', 'motion-note',
      'compare-workbench', 'compare-current', 'compare-reference',
      'compare-left-image', 'compare-right-image',
      'compare-left-image-error', 'compare-right-image-error',
      'compare-left-category', 'compare-right-category',
      'compare-left-look', 'compare-right-look',
      'compare-left-feel', 'compare-right-feel',
      'compare-left-bestFor', 'compare-right-bestFor',
      'compare-left-tradeoff', 'compare-right-tradeoff',
      'compare-match-note', 'compare-status', 'all-materials',
    ];
    const elements = Object.fromEntries(ids.map((id) => [id, root.querySelector(`[id="aml-${id}"]`)]));
    if (ids.some((id) => !elements[id])) return;
    const sameMaterialNote = elements['compare-match-note'].textContent;
    const applications = root.querySelector('#aml-applications');
    const exhibit = elements['swatch-stage'].closest('.aml-material-exhibit');
    if (!exhibit) return;
    root.dataset.amlReady = 'true';

    const reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)');
    const designMode = root.dataset.designMode === 'true';
    const singleMaterial = materials.length === 1;
    root.dataset.single = String(singleMaterial);
    const sectionId = root.dataset.sectionId || root.closest('[id^="shopify-section-"]')?.id || '';
    const pending = designMode ? pendingSelections.get(sectionId) : null;
    if (pending) { window.clearTimeout(pending.timeout); pendingSelections.delete(sectionId); }
    const priorCurrentId = pending?.current || root.dataset.material;
    const priorReferenceId = pending?.reference || root.dataset.referenceMaterial;
    const restoredCurrentIndex = materials.findIndex((material) => material.id === priorCurrentId);
    const configuredCurrentIndex = materials.findIndex((material) => material.defaultCurrent);
    const initialIndex = restoredCurrentIndex >= 0 ? restoredCurrentIndex : Math.max(0, configuredCurrentIndex);
    const restoredReferenceIndex = materials.findIndex((material) => material.id === priorReferenceId);
    const configuredReferenceIndex = materials.findIndex((material) => material.defaultReference);
    const fallbackReferenceIndex = Math.max(0, materials.findIndex((material, index) => index !== initialIndex));
    const initialReferenceIndex = restoredReferenceIndex >= 0 ? restoredReferenceIndex
      : configuredReferenceIndex >= 0 ? configuredReferenceIndex : fallbackReferenceIndex;
    const MATERIAL_PASS_MS = 7500;
    const DOCK_DURATION_MS = 650;
    const state = {
      activeIndex: initialIndex,
      referenceIndex: initialReferenceIndex,
      phase: initialIndex,
      motionAllowed: !reducedMotion.matches && !designMode && !singleMaterial,
      userPaused: false,
      stageVisible: false,
      readingDetails: false,
      dialogOpen: false,
      showcaseReason: '',
    };
    const cards = [];
    const tabs = [];
    const failedImages = new Set();
    const controller = new AbortController();
    const { signal } = controller;
    let pointerStart = null;
    let completedPointer = null;
    let suppressStageClickUntil = 0;
    let illustrationReturnFocus = null;
    let animationFrame = null;
    let previousFrameTime = null;
    let docking = null;
    let railSpacing = 260;
    let railExit = 460;
    const surfaceModuleUrl = root.dataset.surfaceModule;
    const surfaceHost = root.querySelector('#aml-texture-surface');
    const surfaceTools = root.querySelector('[data-aml-surface-tools]');
    const surfaceControls = root.querySelector('#aml-texture-surface-controls');
    const surfaceStatus = root.querySelector('#aml-texture-surface-status');
    const surfaceMode = root.querySelector('#aml-texture-mode');
    const surfaceLight = root.querySelector('#aml-texture-light');
    const surfaceReset = root.querySelector('#aml-texture-reset');
    let surfaceController = null;
    let surfaceView = null;
    const on = (target, event, handler, options = {}) => target.addEventListener(event, handler, { ...options, signal });
    const create = (tag, className, text) => {
      const node = document.createElement(tag);
      if (className) node.className = className.split(/\s+/).map((name) => `aml-${name}`).join(' ');
      if (text !== undefined) node.textContent = text;
      return node;
    };
    const currentMaterial = () => materials[state.activeIndex];
    const imageAvailable = (material) => Boolean(typeof material.image === 'string' && material.image.trim() && !failedImages.has(material.id));
    const wrapIndex = (index) => ((index % materials.length) + materials.length) % materials.length;

    function relativeOffset(index, center = state.phase) {
      const forward = wrapIndex(index - center);
      return forward > materials.length / 2 ? forward - materials.length : forward;
    }

    function updateInspectionAvailability() {
      elements['inspect-texture'].disabled = !imageAvailable(currentMaterial());
    }

    function markImageUnavailable(material) {
      failedImages.add(material.id);
      [...cards, ...tabs].filter((button) => button.dataset.materialId === material.id).forEach((button) => {
        button.dataset.imageError = 'true';
        const image = button.querySelector('img');
        if (image) image.hidden = true;
        const placeholder = button.querySelector('.aml-image-placeholder');
        if (placeholder) placeholder.hidden = false;
      });
      ['left', 'right'].forEach((side) => {
        const image = elements[`compare-${side}-image`];
        if (image.dataset.materialId !== material.id) return;
        image.dataset.imageError = 'true';
        image.hidden = true;
        const error = elements[`compare-${side}-image-error`];
        error.textContent = ui.surface_image_unavailable_details;
        error.hidden = false;
      });
      updateInspectionAvailability();
    }

    function materialImage(material, className, button) {
      const holder = create('span', `image-holder ${className}-holder`);
      const placeholder = create('span', 'image-placeholder', ui.surface_image_unavailable);
      placeholder.setAttribute('aria-hidden', 'true');
      const image = create('img', className);
      image.alt = material.alt || formatUi('material_surface_detail', { material: material.name });
      image.width = 640;
      image.height = 853;
      image.draggable = false;
      image.decoding = 'async';
      image.loading = material.id === materials[initialIndex].id ? 'eager' : 'lazy';
      if (material.id === materials[initialIndex].id) image.fetchPriority = 'high';
      on(image, 'error', () => markImageUnavailable(material));
      on(image, 'load', () => {
        if (!imageAvailable(material)) return;
        delete button.dataset.imageError;
        image.hidden = false;
        placeholder.hidden = true;
      });
      const available = imageAvailable(material);
      image.hidden = !available;
      placeholder.hidden = available;
      if (available) image.src = material.image;
      else button.dataset.imageError = 'true';
      holder.append(image, placeholder);
      return holder;
    }

    function buildSelectors() {
      const cardFragment = document.createDocumentFragment();
      const existingTabs = new Map([...elements['material-tabs'].querySelectorAll('.aml-material-tab[data-material-id]')]
        .map((tab) => [tab.dataset.materialId, tab]));
      materials.forEach((material, index) => {
        const card = create('button', 'sample-card');
        card.type = 'button';
        card.dataset.materialId = material.id;
        card.setAttribute('aria-label', formatUi('select_material', { material: material.name }));
        const foot = create('span', 'card-foot');
        foot.append(create('span', 'card-code', material.index), create('span', 'card-name', material.shortName));
        const body = create('span', 'swatch-body');
        const back = create('span', 'swatch-back');
        back.setAttribute('aria-hidden', 'true');
        const edges = ['left', 'right', 'bottom'].map((side) => {
          const edge = create('span', `swatch-edge swatch-edge-${side}`);
          edge.setAttribute('aria-hidden', 'true');
          return edge;
        });
        body.append(back, ...edges, materialImage(material, 'card-image', card));
        card.append(body, foot);
        on(card, 'click', () => selectMaterial(index));
        cards.push(card);
        cardFragment.append(card);

        // Reuse the Liquid button itself: Shopify owns its block attributes.
        const tab = existingTabs.get(material.id);
        if (!tab) return;
        tab.type = 'button';
        tab.dataset.materialId = material.id;
        tab.setAttribute('aria-label', formatUi('select_material', { material: material.name }));
        const tabCopy = create('span', 'tab-copy');
        tabCopy.append(create('span', 'tab-index', material.index), create('span', 'tab-name', material.shortName));
        tab.replaceChildren(materialImage(material, 'tab-swatch', tab), tabCopy);
        on(tab, 'click', () => selectMaterial(index));
        on(tab, 'keydown', (event) => {
          let targetIndex;
          if (event.key === 'ArrowRight') targetIndex = wrapIndex(index + 1);
          if (event.key === 'ArrowLeft') targetIndex = wrapIndex(index - 1);
          if (event.key === 'Home') targetIndex = 0;
          if (event.key === 'End') targetIndex = materials.length - 1;
          if (targetIndex === undefined) return;
          event.preventDefault();
          tabs[targetIndex].focus({ preventScroll: true });
          selectMaterial(targetIndex);
        });
        tabs.push(tab);
      });
      elements['material-cards'].replaceChildren(cardFragment);
      elements['material-tabs'].setAttribute('role', 'group');
    }

    function cacheStageDimensions() {
      const cardWidth = cards[0]?.offsetWidth || 242;
      const stageWidth = elements['swatch-stage'].clientWidth || 640;
      railSpacing = cardWidth + 18;
      railExit = stageWidth / 2 + cardWidth / 2;
    }

    function updateCardPositions() {
      cards.forEach((card, index) => {
        const relative = relativeOffset(index);
        const x = relative * railSpacing;
        const yaw = Math.max(-14, Math.min(14, -relative * 9));
        const opacity = Math.max(0, Math.min(1, (railExit - Math.abs(x)) / 28));
        const visible = opacity > 0.03;
        if (card.dataset.visible !== String(visible)) {
          card.dataset.visible = String(visible);
          card.tabIndex = visible ? 0 : -1;
          card.setAttribute('aria-hidden', String(!visible));
          card.inert = !visible;
          card.style.pointerEvents = visible ? '' : 'none';
        }
        card.style.transform = `translate3d(${x.toFixed(3)}px,0,${(50 - Math.abs(relative) * 28).toFixed(3)}px) rotateY(${yaw.toFixed(3)}deg)`;
        card.style.zIndex = String(Math.round(100 - Math.abs(relative) * 10));
        card.style.opacity = opacity.toFixed(3);
      });
    }

    function keepSelectedTabVisible() {
      const container = elements['material-tabs'];
      const tab = tabs[state.activeIndex];
      if (!tab || !tabs[0]) return;
      const padding = Number.parseFloat(window.getComputedStyle(container).paddingLeft) || 0;
      const left = tab.offsetLeft - tabs[0].offsetLeft + padding;
      const right = left + tab.offsetWidth;
      const visibleLeft = container.scrollLeft;
      const visibleRight = visibleLeft + container.clientWidth;
      let nextLeft = visibleLeft;
      if (left < visibleLeft) nextLeft = Math.max(0, left - padding);
      else if (right > visibleRight) nextLeft = right - container.clientWidth + padding;
      if (nextLeft !== visibleLeft) container.scrollTo({ left: nextLeft, behavior: 'auto' });
    }

    function renderDetails(material) {
      const textFields = {
        'selected-index': `${material.index} / ${String(materials.length).padStart(2, '0')}`,
        'selected-name': material.name,
        'selected-category': material.category,
        'selected-headline': material.headline,
        'material-description': material.description,
        'material-note': material.note,
        'image-reference-note': material.imageCaption,
        'application-material': material.name,
      };
      Object.entries(textFields).forEach(([id, value]) => { elements[id].textContent = value; });
      elements['material-properties'].replaceChildren(...material.properties.map(({ label, value }) => {
        const row = create('div', 'property-row');
        row.append(create('dt', null, label), create('dd', null, value));
        return row;
      }));
      elements['application-list'].replaceChildren(...material.uses.map(({ label, note, href }) => {
        const link = create('a', 'application-link');
        link.href = href;
        const text = create('span', 'application-copy');
        text.append(create('span', 'application-label', label), create('p', 'application-note', note), create('span', 'application-cta', root.dataset.labelProductCta || ''));
        const arrow = create('span', 'arrow', '↗');
        arrow.setAttribute('aria-hidden', 'true');
        link.append(text, arrow);
        return link;
      }));
      if (applications) applications.hidden = root.dataset.productsEnabled === 'false' || material.uses.length === 0;
      elements['sample-checks'].replaceChildren(...material.sampleChecks.map((check) => create('li', null, check)));
      elements['focus-sheet'].dataset.material = material.id;
      elements['brief-status'].textContent = '';
    }

    function renderComparisonSide(side, material) {
      const image = elements[`compare-${side}-image`];
      const imageError = elements[`compare-${side}-image-error`];
      elements[`compare-${side}-category`].textContent = material.category;
      ['look', 'feel', 'bestFor', 'tradeoff'].forEach((field) => {
        elements[`compare-${side}-${field}`].replaceChildren(
          create('span', 'sr-only', `${material.name}: `),
          document.createTextNode(material.comparison[field]),
        );
      });
      image.alt = material.alt || formatUi('material_surface_detail', { material: material.name });
      if (image.dataset.materialId !== material.id) {
        image.dataset.materialId = material.id;
        delete image.dataset.imageError;
        const available = imageAvailable(material);
        image.hidden = !available;
        imageError.hidden = available;
        if (available) image.src = material.image;
        else {
          image.removeAttribute('src');
          imageError.textContent = ui.surface_image_unavailable_details;
        }
      }
    }

    function updateComparisonMatch() {
      const current = currentMaterial();
      const reference = materials[state.referenceIndex];
      const sameMaterial = current.id === reference.id;
      const workbench = elements['compare-workbench'];
      workbench.dataset.currentMaterial = current.id;
      workbench.dataset.referenceMaterial = reference.id;
      workbench.dataset.sameMaterial = String(sameMaterial);
      root.dataset.referenceMaterial = reference.id;
      elements['compare-match-note'].textContent = sameMaterialNote;
      elements['compare-match-note'].hidden = singleMaterial || !sameMaterial;
    }

    function renderCurrentComparison(material) {
      elements['compare-current'].value = material.id;
      renderComparisonSide('left', material);
      updateComparisonMatch();
    }

    function announceComparison() {
      elements['compare-status'].textContent = formatUi('comparing', { current: currentMaterial().name, reference: materials[state.referenceIndex].name });
    }

    function buildComparison() {
      ['compare-current', 'compare-reference'].forEach((id) => {
        elements[id].replaceChildren(...materials.map((material) => {
          const option = create('option', null, material.shortName);
          option.title = material.name;
          option.value = material.id;
          return option;
        }));
      });
      ['left', 'right'].forEach((side) => {
        const image = elements[`compare-${side}-image`];
        const imageError = elements[`compare-${side}-image-error`];
        on(image, 'error', () => {
          const material = materials.find((item) => item.id === image.dataset.materialId);
          if (material) markImageUnavailable(material);
        });
        on(image, 'load', () => {
          const material = materials.find((item) => item.id === image.dataset.materialId);
          if (!material || !imageAvailable(material)) return;
          delete image.dataset.imageError;
          image.hidden = false;
          imageError.hidden = true;
        });
      });
      elements['compare-reference'].value = materials[state.referenceIndex].id;
      renderComparisonSide('right', materials[state.referenceIndex]);
      on(elements['compare-current'], 'change', (event) => {
        const index = materials.findIndex((material) => material.id === event.target.value);
        if (index < 0) return;
        // A is the same material selected above, not an independent copy.
        selectMaterial(index);
        announceComparison();
      });
      on(elements['compare-reference'], 'change', (event) => {
        const index = materials.findIndex((material) => material.id === event.target.value);
        if (index < 0) return;
        pauseShowcase();
        state.referenceIndex = index;
        renderComparisonSide('right', materials[index]);
        updateComparisonMatch();
        announceComparison();
      });
    }

    function renderSelection(index, announce = true) {
      state.activeIndex = wrapIndex(index);
      const material = currentMaterial();
      root.dataset.material = material.id;
      cards.forEach((card, cardIndex) => {
        const active = cardIndex === state.activeIndex;
        card.classList.toggle('is-active', active);
        card.dataset.active = String(active);
        card.setAttribute('aria-pressed', String(active));
      });
      tabs.forEach((tab, tabIndex) => {
        const active = tabIndex === state.activeIndex;
        tab.classList.toggle('is-active', active);
        tab.setAttribute('aria-pressed', String(active));
        tab.tabIndex = active ? 0 : -1;
      });
      renderDetails(material);
      renderCurrentComparison(material);
      updateInspectionAvailability();
      keepSelectedTabVisible();
      if (elements['texture-dialog'].open) updateIllustration();
      if (announce) elements['material-status'].textContent = formatUi('selected', { material: material.name });
    }

    function selectMaterial(index, announce = true) {
      if (announce) pauseShowcase();
      const targetIndex = wrapIndex(index);
      renderSelection(targetIndex, announce);
      const distance = relativeOffset(targetIndex);
      if (!announce || !state.motionAllowed || !state.stageVisible || document.hidden || state.readingDetails || Math.abs(distance) < 0.001) {
        state.phase = targetIndex;
        docking = null;
        updateCardPositions();
      } else {
        docking = { from: state.phase, distance, elapsed: 0 };
      }
      syncShowcase();
    }

    function stopAnimationFrame() {
      if (animationFrame !== null) window.cancelAnimationFrame(animationFrame);
      animationFrame = null;
      previousFrameTime = null;
    }

    function canAnimate() {
      return !designMode && !singleMaterial && state.motionAllowed && !document.hidden && state.stageVisible && !state.readingDetails && !state.dialogOpen;
    }

    function frame(timestamp) {
      animationFrame = null;
      if (signal.aborted || !canAnimate() || (!docking && showcaseReason() !== 'playing')) {
        previousFrameTime = null;
        return;
      }
      const elapsed = previousFrameTime === null ? 0 : Math.min(100, timestamp - previousFrameTime);
      previousFrameTime = timestamp;
      if (docking) {
        docking.elapsed += elapsed;
        const progress = Math.min(1, docking.elapsed / DOCK_DURATION_MS);
        state.phase = docking.from + docking.distance * (1 - (1 - progress) ** 3);
        if (progress === 1) { state.phase = wrapIndex(state.phase); docking = null; }
      } else {
        state.phase = wrapIndex(state.phase + elapsed / MATERIAL_PASS_MS);
        const nearestIndex = wrapIndex(Math.round(state.phase));
        if (nearestIndex !== state.activeIndex) renderSelection(nearestIndex, false);
      }
      updateCardPositions();
      if (docking || showcaseReason() === 'playing') animationFrame = window.requestAnimationFrame(frame);
      else { previousFrameTime = null; root.dataset.trajectory = 'still'; }
    }

    function showcaseReason() {
      if (designMode) return 'editor';
      if (singleMaterial) return 'single';
      if (!state.motionAllowed) return 'reduced';
      if (document.hidden) return 'hidden';
      if (!state.stageVisible) return 'offscreen';
      if (state.dialogOpen) return 'dialog';
      if (state.userPaused) return 'paused';
      if (state.readingDetails) return 'reading';
      return 'playing';
    }

    function syncShowcase() {
      if (signal.aborted) return;
      const reason = showcaseReason();
      const changed = reason !== state.showcaseReason;
      root.dataset.showcase = reason;
      const shouldAnimate = canAnimate() && (reason === 'playing' || docking !== null);
      root.dataset.trajectory = shouldAnimate ? (docking ? 'docking' : 'rotating') : 'still';
      if (!shouldAnimate) stopAnimationFrame();
      const toggle = elements['motion-toggle'];
      toggle.disabled = !state.motionAllowed;
      const playbackEnabled = state.motionAllowed && !state.userPaused;
      toggle.textContent = playbackEnabled ? ui.pause_showcase : ui.resume_showcase;
      toggle.setAttribute('aria-pressed', String(playbackEnabled));
      toggle.setAttribute('aria-label', playbackEnabled
        ? ui.pause_showcase_aria : ui.resume_showcase_aria);
      if (changed) {
        const notes = {
          editor: ui.motion_editor,
          single: '',
          playing: ui.motion_playing,
          paused: ui.motion_paused,
          reading: ui.motion_reading,
          dialog: ui.motion_dialog,
          reduced: ui.motion_reduced,
          offscreen: state.userPaused
            ? ui.motion_paused
            : ui.motion_offscreen,
          hidden: ui.motion_hidden,
        };
        elements['motion-note'].textContent = notes[reason];
        state.showcaseReason = reason;
      }
      if (shouldAnimate && animationFrame === null) animationFrame = window.requestAnimationFrame(frame);
    }

    function pauseShowcase() {
      state.userPaused = true;
      docking = null;
      syncShowcase();
    }

    function resumeShowcase() {
      if (!state.motionAllowed || state.dialogOpen) return;
      state.userPaused = false;
      docking = null;
      syncShowcase();
    }

    function isOpenShowcaseArea(target) {
      if (!(target instanceof Element)) return false;
      const area = target.closest('#aml-swatch-stage, .aml-exhibit-tools');
      return Boolean(area && exhibit.contains(area)
        && !target.closest('button, a, input, textarea, select, label, summary, [role="button"], [contenteditable="true"]'));
    }

    function updateReadingPosition() {
      const stageBounds = elements['swatch-stage'].getBoundingClientRect();
      const detailsBounds = elements['focus-sheet'].getBoundingClientRect();
      const readingDetails = stageBounds.top < 0 && detailsBounds.top <= window.innerHeight * 0.5;
      if (readingDetails === state.readingDetails) return;
      state.readingDetails = readingDetails;
      syncShowcase();
    }

    function updateMotionPreference() {
      state.motionAllowed = !reducedMotion.matches && !designMode && !singleMaterial;
      root.dataset.motion = state.motionAllowed ? 'on' : 'off';
      if (!state.motionAllowed) { state.phase = state.activeIndex; docking = null; updateCardPositions(); }
      syncShowcase();
    }

    function updateIllustrationScale() {
      const scale = Math.max(1, Math.min(1.7, Number(elements['texture-scale'].value) || 1));
      elements['texture-dialog'].style.setProperty('--texture-scale', scale);
      elements['texture-scale'].setAttribute('aria-valuetext', formatUi('scale_percent', { percent: Math.round(scale * 100) }));
      surfaceView?.setZoom(scale);
    }

    function stopSurface() {
      surfaceController?.abort();
      surfaceController = null;
      surfaceView?.dispose();
      surfaceView = null;
      if (surfaceHost) surfaceHost.hidden = true;
      if (surfaceControls) surfaceControls.hidden = true;
    }

    function showSurfaceImage(unavailable = false) {
      stopSurface();
      elements['texture-dialog'].dataset.surfaceMode = unavailable ? 'unavailable' : 'image';
      elements['texture-image'].hidden = !imageAvailable(currentMaterial());
      if (surfaceStatus) surfaceStatus.textContent = unavailable
        ? 'The surface view is unavailable. You can still enlarge the image.'
        : 'Surface image. Use the slider below to enlarge it.';
      if (surfaceTools) surfaceTools.hidden = false;
      if (surfaceMode) { surfaceMode.hidden = unavailable; surfaceMode.textContent = 'Explore surface'; surfaceMode.disabled = false; }
    }

    async function startSurface() {
      if (!surfaceModuleUrl || !surfaceHost || !surfaceTools || !surfaceControls || !surfaceStatus || !surfaceMode
        || !surfaceLight || !surfaceReset || !state.dialogOpen || !elements['texture-dialog'].open || !imageAvailable(currentMaterial())) return;
      stopSurface();
      const pending = new AbortController();
      surfaceController = pending;
      const material = currentMaterial();
      elements['texture-dialog'].dataset.surfaceMode = 'loading';
      surfaceHost.dataset.surfaceLoading = 'true';
      surfaceHost.hidden = false;
      surfaceTools.hidden = false;
      surfaceStatus.textContent = 'Preparing the surface view…';
      surfaceMode.textContent = 'View image';
      surfaceMode.hidden = false;
      surfaceMode.disabled = true;
      try {
        const { mountMaterialSurface } = await import(surfaceModuleUrl);
        if (pending.signal.aborted || signal.aborted) return;
        const view = await mountMaterialSurface(surfaceHost, {
          image: material.image, name: material.name, alt: material.alt, signal: pending.signal,
        });
        if (pending.signal.aborted || signal.aborted || !state.dialogOpen) { view.dispose(); return; }
        surfaceView = view;
        delete surfaceHost.dataset.surfaceLoading;
        elements['texture-image'].hidden = true;
        elements['texture-dialog'].dataset.surfaceMode = '3d';
        surfaceControls.hidden = false;
        surfaceMode.disabled = false;
        surfaceLight.value = '0';
        surfaceStatus.textContent = 'Drag to turn. Move the light to inspect the surface.';
        updateIllustrationScale();
      } catch (error) {
        if (!pending.signal.aborted && !signal.aborted) showSurfaceImage(true);
      }
    }

    function updateIllustration() {
      stopSurface();
      const material = currentMaterial();
      elements['texture-title'].textContent = formatUi('detail_title', { material: material.name });
      const available = imageAvailable(material);
      elements['texture-error'].hidden = available;
      elements['texture-image'].hidden = !available;
      elements['texture-image'].alt = material.alt || formatUi('material_surface_detail', { material: material.name });
      elements['texture-scale'].disabled = !available;
      if (available) elements['texture-image'].src = material.image;
      else elements['texture-image'].removeAttribute('src');
      elements['texture-scale'].value = '1';
      updateIllustrationScale();
      if (elements['texture-dialog'].open) startSurface();
    }

    async function copyBrief() {
      const material = currentMaterial();
      const brief = [
        formatUi('brief_material', { material: material.name }),
        ui.brief_product,
        ui.brief_colour,
        ui.brief_thickness,
        ui.brief_logo,
        ui.brief_quantity, '',
        ui.brief_sample_review,
        ...material.sampleChecks.map((check, index) => `${index + 1}. ${check}`),
      ].join('\n');
      let copied = false;
      try {
        if (navigator.clipboard?.writeText) { await navigator.clipboard.writeText(brief); copied = true; }
      } catch { /* Local copy fallback; no information is transmitted. */ }
      if (!copied) {
        const previousFocus = document.activeElement;
        const textarea = create('textarea', 'copy-buffer');
        textarea.value = brief;
        textarea.readOnly = true;
        root.append(textarea);
        try { textarea.select(); textarea.setSelectionRange(0, brief.length); copied = document.execCommand('copy'); }
        catch { copied = false; }
        finally {
          textarea.remove();
          if (previousFocus instanceof HTMLElement) previousFocus.focus({ preventScroll: true });
        }
      }
      elements['brief-status'].textContent = copied
        ? formatUi('copied', { material: material.name })
        : ui.copy_unavailable;
    }

    buildSelectors();
    buildComparison();
    on(elements['previous-material'], 'click', () => selectMaterial(state.activeIndex - 1));
    on(elements['next-material'], 'click', () => selectMaterial(state.activeIndex + 1));
    on(elements['motion-toggle'], 'click', () => {
      if (!state.motionAllowed) return;
      state.userPaused = !state.userPaused;
      docking = null;
      syncShowcase();
    });
    on(root, 'focusin', (event) => {
      if (event.target !== elements['motion-toggle']) pauseShowcase();
    });
    on(root, 'pointerdown', (event) => {
      // Record before pausing: the press freezes the rail, while only a completed
      // short click on display/tool background may resume it. Reading stays paused.
      completedPointer = null;
      pointerStart = event.isPrimary && event.button === 0
        && (elements['swatch-stage'].contains(event.target) || isOpenShowcaseArea(event.target))
        ? { id: event.pointerId, x: event.clientX, y: event.clientY, time: performance.now(),
          stage: elements['swatch-stage'].contains(event.target), openArea: isOpenShowcaseArea(event.target) }
        : null;
      if (!elements['motion-toggle'].contains(event.target)) pauseShowcase();
    }, { capture: true });
    on(reducedMotion, 'change', updateMotionPreference);
    on(window, 'scroll', updateReadingPosition, { passive: true });
    on(window, 'resize', () => {
      cacheStageDimensions();
      updateCardPositions();
      updateReadingPosition();
    });
    on(document, 'visibilitychange', () => {
      if (document.hidden) { pointerStart = null; completedPointer = null; }
      syncShowcase();
    });
    on(window, 'pointerup', (event) => {
      if (!pointerStart || event.pointerId !== pointerStart.id) return;
      const press = pointerStart;
      const dx = event.clientX - press.x;
      const dy = event.clientY - press.y;
      const heldFor = performance.now() - press.time;
      pointerStart = null;
      completedPointer = null;
      if (heldFor > 550 || (Math.abs(dy) > 12 && Math.abs(dy) >= Math.abs(dx))) {
        suppressStageClickUntil = performance.now() + 350;
        return;
      }
      if (press.stage && Math.abs(dx) >= 55 && Math.abs(dx) > Math.abs(dy) * 1.2) {
        suppressStageClickUntil = performance.now() + 350;
        selectMaterial(state.activeIndex + (dx < 0 ? 1 : -1));
        return;
      }
      if (Math.hypot(dx, dy) > 8) {
        suppressStageClickUntil = performance.now() + 350;
        return;
      }
      if (press.openArea && isOpenShowcaseArea(event.target)) {
        completedPointer = { time: performance.now() };
      }
    });
    on(window, 'pointercancel', () => {
      if (pointerStart) suppressStageClickUntil = performance.now() + 350;
      pointerStart = null;
      completedPointer = null;
    });
    on(exhibit, 'click', (event) => {
      if (elements['swatch-stage'].contains(event.target)
        && performance.now() < suppressStageClickUntil && event.detail !== 0) {
        completedPointer = null;
        event.preventDefault();
        event.stopImmediatePropagation();
        return;
      }
      const press = completedPointer;
      completedPointer = null;
      if (!press || performance.now() - press.time > 350 || !isOpenShowcaseArea(event.target)) return;
      const selection = window.getSelection();
      if (selection && !selection.isCollapsed) return;
      // Blank clicks are a resume-only shortcut, never a play/pause toggle.
      // Material controls, text, links, dialogs and the reading area keep their own actions.
      resumeShowcase();
    }, { capture: true });
    on(elements['texture-image'], 'load', () => {
      if (!imageAvailable(currentMaterial())) return;
      elements['texture-error'].hidden = true;
      elements['texture-image'].hidden = Boolean(surfaceView);
    });
    on(elements['texture-image'], 'error', () => {
      markImageUnavailable(currentMaterial());
      elements['texture-image'].hidden = true;
      elements['texture-error'].hidden = false;
      elements['texture-scale'].disabled = true;
    });
    on(elements['inspect-texture'], 'click', () => {
      if (!imageAvailable(currentMaterial())) return;
      state.dialogOpen = true;
      pauseShowcase();
      illustrationReturnFocus = document.activeElement;
      updateIllustration();
      if (!elements['texture-dialog'].open) elements['texture-dialog'].showModal();
      elements['texture-close'].focus({ preventScroll: true });
      startSurface();
    });
    on(elements['texture-close'], 'click', () => elements['texture-dialog'].close());
    on(elements['texture-dialog'], 'click', (event) => {
      if (event.target !== elements['texture-dialog']) return;
      const bounds = elements['texture-dialog'].getBoundingClientRect();
      if (event.clientX < bounds.left || event.clientX > bounds.right || event.clientY < bounds.top || event.clientY > bounds.bottom) elements['texture-dialog'].close();
    });
    on(elements['texture-dialog'], 'close', () => {
      stopSurface();
      state.dialogOpen = false;
      syncShowcase();
      if (illustrationReturnFocus instanceof HTMLElement) illustrationReturnFocus.focus({ preventScroll: true });
    });
    on(elements['texture-scale'], 'input', updateIllustrationScale);
    if (surfaceMode) on(surfaceMode, 'click', () => surfaceView ? showSurfaceImage() : startSurface());
    if (surfaceLight) on(surfaceLight, 'input', () => surfaceView?.setLight(Number(surfaceLight.value)));
    if (surfaceReset) on(surfaceReset, 'click', () => {
      surfaceView?.reset();
      elements['texture-scale'].value = '1';
      surfaceLight.value = '0';
      updateIllustrationScale();
    });
    if (surfaceHost) on(surfaceHost, 'aml:surface-error', () => showSurfaceImage(true));
    if (surfaceHost) on(surfaceHost, 'keydown', (event) => {
      if (event.key.toLowerCase() !== 'r' && event.key !== 'Home') return;
      elements['texture-scale'].value = '1';
      if (surfaceLight) surfaceLight.value = '0';
      updateIllustrationScale();
    });
    on(elements['copy-brief'], 'click', copyBrief);
    on(document, 'shopify:block:select', (event) => {
      if (!designMode) return;
      if (event.detail?.sectionId && sectionId && event.detail.sectionId !== sectionId) return;
      const blockId = event.detail?.blockId;
      const index = materials.findIndex((material) => material.id === blockId);
      if (index >= 0) selectMaterial(index);
    });
    on(document, 'shopify:section:unload', (event) => {
      if (!event.target.contains(root)) return;
      if (designMode && sectionId) {
        const prior = pendingSelections.get(sectionId);
        if (prior) window.clearTimeout(prior.timeout);
        pendingSelections.set(sectionId, {
          current: currentMaterial().id,
          reference: materials[state.referenceIndex].id,
          timeout: window.setTimeout(() => pendingSelections.delete(sectionId), 30000),
        });
      }
      stopAnimationFrame();
      stopSurface();
      docking = null;
      stageObserver?.disconnect();
      controller.abort();
      root.dataset.showcase = 'paused';
      delete root.dataset.amlReady;
    });
    const stageObserver = 'IntersectionObserver' in window ? new IntersectionObserver((entries) => {
      if (signal.aborted) return;
      state.stageVisible = entries.some((entry) => entry.isIntersecting && entry.intersectionRatio >= 0.35);
      updateReadingPosition();
      syncShowcase();
    }, { threshold: [0, 0.35] }) : null;
    if (stageObserver) stageObserver.observe(elements['swatch-stage']);
    else state.userPaused = true;
    cacheStageDimensions();
    selectMaterial(initialIndex, false);
    elements['compare-workbench'].hidden = singleMaterial;
    elements['all-materials'].open = singleMaterial;
    ['previous-material', 'next-material', 'motion-toggle'].forEach((id) => {
      elements[id].hidden = singleMaterial;
      if (singleMaterial) elements[id].disabled = true;
    });
    updateReadingPosition();
    updateMotionPreference();
  }

  function initializeWithin(container) {
    if (container.matches?.('[data-aml-live]')) initializeMaterialLab(container);
    container.querySelectorAll('[data-aml-live]').forEach(initializeMaterialLab);
  }
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', () => initializeWithin(document), { once: true });
  else initializeWithin(document);
  document.addEventListener('shopify:section:load', (event) => initializeWithin(event.target));
})();
